import { AdditionalItem, AdditionalLabel } from './AssignmentDropdownItems';
import { generateRandomId } from '../../helpers/helpers';

export default {};

export const getType = (field = {}, includeSubDataType = true) => {
  let {
    configProps: {
      dataType,
      subDataType, // eslint-disable-line prefer-const
    } = {},
    selectedType: mainFieldType,
  } = field;
  if (mainFieldType === 'dynamicAttribute') {
    mainFieldType = field.configProps?.fieldType;
    dataType = field.configProps?.fieldDataType;
  }
  // Attributes and calculations turn to text
  let type = mainFieldType === 'attribute' || mainFieldType === 'calculation' ? 'text' : mainFieldType;
  if (type === 'table' || type === 'dropdown') {
    if (includeSubDataType && (dataType === 'CompletedForms' || dataType === 'Buckets')) {
      type += ` - ${dataType} - ${subDataType}`;
    } else {
      type += ` - ${dataType}`;
    }
  }
  return type;
};

export const getSourceFieldMap = (
  sourceSections = [],
  parentLogicalLoopNode = null,
  formDropdownSubTypes = [],
  { mapDropdown = false } = {},
) => {
  // Map fields by type
  const fieldMap = {};
  sourceSections.forEach((section) => {
    const { fields: sectionFields = [], name: sectionName } = section;
    sectionFields.forEach((field) => {
      const {
        configProps: {
          title: fieldTitle,
          subDataType = [],
        } = {},
        id: fieldId,
      } = field;

      const type = getType(field);
      if (!(type in fieldMap)) {
        fieldMap[type] = [];
      }

      const fieldEntry = {
        ...field,
        title: `${sectionName} - ${fieldTitle}`,
        fieldTitle,
      };
      fieldMap[type].push(fieldEntry);

      // Opt-in (`mapDropdown`): aggregate key `dropdown` for targets using types: ['dropdown']
      // (e.g. board card / dispatch title).
      if (mapDropdown && field.selectedType === 'dropdown') {
        if (!('dropdown' in fieldMap)) fieldMap.dropdown = [];
        fieldMap.dropdown.push(fieldEntry);
      }

      if (parentLogicalLoopNode?.data?.field === fieldId) {
        let sourceType = type;
        let displayWarning = false;

        if (
          parentLogicalLoopNode?.data.mapLabelToEquipment
          && type === 'dropdown - Labels'
          && subDataType?.includes('equipment')
        ) {
          sourceType = 'dropdown - Equipment';
          displayWarning = true;
        }

        if (!(sourceType in fieldMap)) {
          fieldMap[sourceType] = [];
        }

        fieldMap[sourceType].push({
          ...field,
          id: `logicalLoop.${parentLogicalLoopNode.id}`,
          title: `${sectionName} - ${fieldTitle}`,
          fieldTitle,
          helpText: 'This option will use the respective values in the loop',
          displayWarning,
        });
      }
    });
  });

  formDropdownSubTypes?.forEach((subtype) => {
    const key = `dropdown - CompletedForms - ${subtype}`;
    const existing = fieldMap[key] ?? [];
    fieldMap[key] = existing.concat([{
      id: 'parentCompletedFormsDropdown',
      title: 'Parent Form',
      helpText: 'This option will load the parent form into the dropdown',
    }]);
  });

  fieldMap['form - PO Value'] = [{
    id: 'poValue',
    title: 'PO Value',
    helpText: 'This option will load the native PO Value. Note that it will ignore all materials during the sync.',
  }];

  return fieldMap;
};

export const updateData = (id, newData) => (dataMap = {}) => {
  const {
    [id]: oldData = {},
  } = dataMap;

  return {
    ...dataMap,
    [id]: {
      ...oldData,
      ...newData,
    },
  };
};

const USER_HELP = `The new form will be assigned to the user's selected in the configured form field`;
const userDropdownTitle = (title) => `Users selected in ${title} field`;
const UserDropdownLabel = ({ title }) => (
  <AdditionalLabel
    title={title}
    help={USER_HELP}
  />
);
const UserItem = ({ title }) => (
  <AdditionalItem
    title={userDropdownTitle(title)}
    help={USER_HELP}
  />
);

export const locateWorkflowParentElement = (id, elementType, elements = []) => {
  const targetToSourceMap = {};
  const elementMap = {};
  const relevantElements = [];
  let logicalLoopElement;

  elements.forEach((element) => {
    elementMap[element.id] = element;

    if (element.type === 'customEdge') {
      if (!targetToSourceMap[element.target]) {
        targetToSourceMap[element.target] = [];
      }
      targetToSourceMap[element.target].push(element.source);
    }

    if (element.id === id) {
      relevantElements.push(element);
    }
  });

  while (relevantElements.length && !logicalLoopElement) {
    const elementToCheck = relevantElements.pop();

    // eslint-disable-next-line no-continue
    if (!elementToCheck) continue;

    if (elementToCheck.type === elementType) {
      logicalLoopElement = elementToCheck;
    } else if (elementMap[targetToSourceMap[elementToCheck.id]]) {
      relevantElements.push(elementMap[targetToSourceMap[elementToCheck.id]]);
    }
  }

  return logicalLoopElement;
};

export const isDynamicType = (field, type, subType) => {
  const {
    selectedType,
    configProps: {
      fieldType,
      fieldDataType,
    } = {},
  } = field ?? {};
  return (
    selectedType === 'dynamicAttribute'
    && fieldType === type
    && (!subType || fieldDataType === subType)
  );
};

export const getUserFields = (sections = []) => {
  const userDropdowns = [];
  sections.forEach(({ fields = [] }) => {
    fields.forEach((field) => {
      const {
        id: fieldId,
        selectedType,
        configProps: {
          dataType,
          title,
        } = {},
      } = field;
      const canAddField = (
        selectedType === 'dropdown' && dataType === 'Users'
      ) || isDynamicType(field, 'dropdown', 'Users');
      if (canAddField) {
        userDropdowns.push({
          id: fieldId,
          label: <UserDropdownLabel title={title}/>,
          children: <UserItem title={title}/>
        });
      }
    });
  });
  return userDropdowns;
};

/**
 * Creates a new status object to add during form template creation
 * @param {string} name - status name
 * @returns {object} newly created status
 */
export const createNewStatus = (name) => {
  const newStatusId = generateRandomId();
  return {
    id: newStatusId,
    statusId: newStatusId,
    isNew: true,
    status: name,
  };
};

/**
 * Builds a map of fieldId -> columns for all table fields of the given type in the given sections.
 * @param {Array} sections - form sections
 * @param {string} tableFieldType - table field type (e.g. 'table - Equipment')
 * @returns {Object} map of fieldId to columns array
 */
export const getTableFieldColumnsMap = ({
  sections = [],
  tableFieldType,
}) => {
  const map = {};
  sections.forEach(({ fields = [] }) => {
    (fields ?? []).forEach((field) => {
      const {
        id: fieldId,
        configProps: { columns } = {},
      } = field;
      const type = getType(field);
      if (type === tableFieldType && columns) {
        map[fieldId] = columns;
      }
    });
  });
  return map;
};

/**
 * Returns true if at least one of the table field ids has a column whose key is in columnKeys.
 * @param {Object} tableFieldIdToColumns - map of table field id to columns array
 * @param {Array} tableFieldIds - table field ids
 * @param {Set<string>} columnKeys - column keys to check for (e.g. ['hours', 'mileage'])
 * @returns {boolean}
 */
export const selectedFieldsHaveColumn = ({
  tableFieldIdToColumns,
  tableFieldIds,
  columnKeys,
}) => {
  const columnKeySet = columnKeys instanceof Set ? columnKeys : new Set(columnKeys ?? []);
  return tableFieldIds?.some((fieldId) => (
    (tableFieldIdToColumns[fieldId] ?? []).some((col) => columnKeySet.has(col.key))
  ));
};

/**
 * Returns true if any equipment table in the division's custom timecard fields has a column
 * whose key is in columnKeys.
 *
 * Used for time entry table checks: the selected time entry table field IDs come from the form,
 * but the equipment column data lives in the division's custom timecard fields — the two ID
 * spaces don't overlap, so we check across all custom-field equipment tables instead.
 *
 * @param {Object} tableFieldIdToColumns - map of equipment field id to columns array (from custom fields)
 * @param {Set<string>} columnKeys - column keys to check for (e.g. ['hours', 'mileage'])
 * @returns {boolean}
 */
export const timeEntryTableFieldsHaveColumn = ({
  tableFieldIdToColumns,
  columnKeys,
}) => {
  const columnKeySet = columnKeys instanceof Set ? columnKeys : new Set(columnKeys ?? []);
  return Object.values(tableFieldIdToColumns).some((columns) => (
    columns.some((col) => columnKeySet.has(col.key))
  ));
};
